import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { GlassPage } from './chrome';

// Shared building blocks. Every page pulls its headings, card grids and legal
// layout from here so there is one definition of each, not one per page.

export const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 ${className}`}>
    {children}
  </p>
);

export const SectionHeading = ({ eyebrow, title, lead, align = 'left', className = '' }) => {
  const centered = align === 'center';
  return (
    <div className={`${centered ? 'max-w-2xl mx-auto text-center' : 'max-w-2xl'} ${className}`}>
      {eyebrow && <Eyebrow className="mb-3 md:mb-4">{eyebrow}</Eyebrow>}
      <h2 className="text-[2rem] leading-[1.05] sm:text-4xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900">
        {title}
      </h2>
      {lead && <p className="mt-4 md:mt-5 text-[15px] md:text-lg text-zinc-600 leading-relaxed">{lead}</p>}
    </div>
  );
};

// Top-of-page title block shared by contact, careers, team and the legal pages.
export const PageHero = ({ title, lead, meta }) => (
  <section className="px-5 sm:px-6 md:px-12 pt-28 md:pt-40 pb-8 md:pb-12">
    <div className="max-w-[1600px] mx-auto w-full">
      <h1 className="text-[13vw] sm:text-[10vw] md:text-[6vw] font-bold leading-[0.9] tracking-tighter mb-6 md:mb-10 uppercase text-zinc-900">
        {title}
      </h1>
      {lead && <p className="text-base sm:text-lg md:text-xl text-zinc-600 font-medium max-w-2xl">{lead}</p>}
      {meta && <p className="text-sm text-zinc-500">{meta}</p>}
    </div>
  </section>
);

// Narrow single-card page: 404, unsubscribe, order confirmation.
export const CenteredCard = ({ children, className = '' }) => (
  <GlassPage>
    <div className="max-w-lg mx-auto px-4 md:px-6 pt-24 md:pt-36">
      <section className={`glass px-6 sm:px-10 md:px-12 py-12 md:py-16 text-center ${className}`}>
        {children}
      </section>
    </div>
  </GlassPage>
);

// Icon + title + copy. A row on phones, where vertical space is the scarce
// thing, and a card once there is room to put them side by side.
// `stacked` puts the icon above the text at every width. The default keeps the
// icon beside the text on phones, where vertical space is the scarce thing.
export const FeatureItem = ({ icon: Icon, title, desc, centered = false, stacked = false, className = '' }) => (
  <div
    className={`card hover-lift p-4 sm:p-5 ${
      stacked ? 'block' : 'flex items-start gap-4 sm:block'
    } ${centered ? 'sm:text-center' : ''} ${className}`}
  >
    <Icon
      size={20}
      strokeWidth={2}
      className={`shrink-0 text-zinc-600 ${stacked ? 'mb-3' : 'mt-0.5 sm:mb-3'} ${
        centered ? 'sm:mx-auto' : ''
      }`}
    />
    <div>
      <h3 className="text-base sm:text-lg font-bold tracking-tight text-zinc-900 mb-1">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
    </div>
  </div>
);

// `swipeOnMobile` turns the phone layout into a snapping side-scroller, so a
// long list costs one card of height instead of one screen per card.
//
// Each card is 76vw and snaps to centre, so one swipe advances one card and
// the neighbours peek evenly on both sides. Sizes are in vw, not percentages,
// because a percentage flex-basis resolves against the track's content box,
// which the padding itself shrinks: the two would chase each other. The
// negative margin cancels `.panel`'s px-5 so the track bleeds to the screen
// edge, leaving it (100vw - 32px) wide from the section's px-4. Centring the
// first and last card therefore needs ((100vw - 32px) - 76vw) / 2 of padding.
// From `sm` up it is the ordinary grid again.
export const FeatureGrid = ({ items, columns = 4, centered = false, swipeOnMobile = false, className = '' }) => {
  const cols = columns === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
  const layout = swipeOnMobile
    ? `no-scrollbar flex items-stretch snap-x snap-mandatory overflow-x-auto gap-4 -mx-5 px-[calc(12vw_-_16px)] sm:mx-0 sm:px-0 sm:overflow-visible sm:grid sm:grid-cols-2 sm:gap-4 md:gap-5 ${cols}`
    : `grid gap-3 sm:grid-cols-2 sm:gap-4 md:gap-5 ${cols}`;

  return (
    <div className={`${layout} ${className}`}>
      {items.map((item) =>
        swipeOnMobile ? (
          <div key={item.title} className="snap-center shrink-0 basis-[76vw] sm:basis-auto sm:shrink">
            {/* No shadow while swiping: `.card`'s blur is wider than the gap,
                so neighbouring shadows overlapped into a dark band with a hard
                edge between cards. Opaque white instead, which separates the
                card from the panel cleanly. The normal card returns at `sm`,
                where the grid spaces them far enough apart. */}
            <FeatureItem
              {...item}
              centered={centered}
              stacked
              className="h-full bg-white shadow-none sm:bg-white/80 sm:shadow-[0_10px_30px_rgba(31,38,135,0.1)]"
            />
          </div>
        ) : (
          <FeatureItem key={item.title} {...item} centered={centered} />
        )
      )}
    </div>
  );
};

export const Accordion = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-2.5 md:space-y-3">
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={item.q} className="rounded-2xl bg-white/70 border border-white overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="w-full text-left px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4"
            >
              <span className="text-[15px] md:text-lg font-bold text-zinc-900 leading-snug">{item.q}</span>
              <Plus
                size={18}
                className={`shrink-0 text-zinc-500 transition-transform duration-300 ease-out ${isOpen ? 'rotate-45' : ''}`}
              />
            </button>
            <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden">
                <p className="px-5 md:px-6 pb-5 text-sm md:text-base text-zinc-600 leading-relaxed">{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Privacy policy and terms are the same page with different words.
export const LegalPage = ({ title, updated, sections }) => (
  <GlassPage>
    <PageHero title={title} meta={`Last Updated: ${updated}`} />
    <section className="px-4 md:px-6 pb-12 md:pb-16">
      <div className="glass max-w-4xl mx-auto px-5 sm:px-8 md:px-12 py-10 md:py-16 space-y-10 md:space-y-12">
        {sections.map((section) => (
          <div key={section.title} className="space-y-3 md:space-y-4">
            <h2 className="text-xl md:text-3xl font-bold tracking-tighter uppercase text-zinc-900">
              {section.title}
            </h2>
            {typeof section.body === 'string'
              ? <p className="text-zinc-600 leading-relaxed">{section.body}</p>
              : section.body}
          </div>
        ))}
      </div>
    </section>
  </GlassPage>
);
